/**
 * 表单字段结果值的结构描述辅助方法
 */

export function buildValueDef({type, key, name, option, fields}) {
  const def = {type, key, name: name || key};
  if (option !== undefined) {
    def.option = option;
  }
  if (Array.isArray(fields) && fields.length > 0) {
    def.fields = fields;
  }
  return def;
}

/**
 * 与 Select/Radio/Check 等控件 options 计算逻辑一致
 * simple 模式：key 与 name 均为选项名称，提交值用名称而非序号/key
 */
export function normalizeSelectOptions(config = {}) {
  let options = config.options;
  if (!options) {
    return [];
  }
  if (!Array.isArray(options)) {
    return [];
  }
  if (options.length && typeof options[0] !== 'object') {
    options = options.map((name, i) => ({
      key: i,
      name: String(name),
    }));
  }
  if (config.simple) {
    options = options.map((v) => {
      const name = v.name != null ? String(v.name) : String(v.key);
      return {key: name, name};
    });
  }
  return options;
}

/** 选择类字段：simple 用 name 作值，否则用 key */
export function selectOptionValueMeta(config = {}) {
  if (config.simple) {
    return {
      simple: true,
      valueAs: 'name',
      description: 'simple 模式：值为选项名称，与 options 中 name 一致（不要用序号或 key）',
    };
  }
  return {
    simple: false,
    valueAs: 'key',
    description: '值为选项 key，见 options[].key',
  };
}

export function stringValueDef(field) {
  return buildValueDef({
    type: 'string',
    key: field.key,
    name: field.name,
    option: field.config || {},
  });
}

export function numberValueDef(field) {
  return buildValueDef({
    type: 'number',
    key: field.key,
    name: field.name,
    option: field.config || {},
  });
}

export function selectValueDef(field) {
  const cfg = field.config || {};
  const multiple = !!cfg.multiple;
  return buildValueDef({
    type: multiple ? 'array' : 'string',
    key: field.key,
    name: field.name,
    option: {
      multiple,
      ...selectOptionValueMeta(cfg),
      options: normalizeSelectOptions(cfg),
      url: cfg.url,
    },
  });
}

export function arrayValueDef(field, option = {}) {
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option,
  });
}

export function fileValueDef(field) {
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      description: '文件列表，元素为文件对象或文件 id',
      ...(field.config || {}),
    },
  });
}

export function objectValueDef(field, fields, option = {}) {
  return buildValueDef({
    type: 'object',
    key: field.key,
    name: field.name,
    option,
    fields,
  });
}

/** 容器控件不产生独立字段值 */
export function containerValueDef() {
  return null;
}

/** 仅展示、无提交值的控件 */
export function noValueDef() {
  return null;
}

const CONTAINER_CONTROLS = new Set([
  'UgoRow', 'UgoSection', 'UgoTabs', 'UgoCol',
  'ZzlRow', 'ZzlSection', 'ZzlTabs', 'ZzlCol',
]);

const SKIP_VALUE_CONTROLS = new Set(['UgoStatic', 'UgoOption', 'UgoRef']);

const SCHEMA_CONTROL_TYPE = {
  UgoInput: 'string',
  UgoTextarea: 'string',
  UgoNumber: 'number',
  UgoDate: 'string',
  UgoDatetime: 'string',
  UgoMonth: 'string',
  UgoSelect: 'string',
  UgoRadio: 'string',
  UgoCheck: 'array',
  UgoMultiSelect: 'array',
  UgoMarkdown: 'string',
  UgoSignature: 'string',
  UgoFile: 'array',
  UgoFileUpload: 'array',
  UgoImages: 'string',
  UgoUserSelect: 'object',
  UgoMark: 'number',
  UgoTable: 'array',
  UgoZzlTable: 'array',
  UgoInnerForm: 'object',
  UgoMultiForm: 'array',
};

/**
 * 无组件实例时按字段配置生成 valueDef（保证 key 来自 field.key）
 */
export function schemaValueDefFromField(field, collectChildren) {
  if (!field || !field.key) {
    return null;
  }
  if (SKIP_VALUE_CONTROLS.has(field.control)) {
    return null;
  }
  if (CONTAINER_CONTROLS.has(field.control)) {
    return typeof collectChildren === 'function' ?
      collectChildren(field.fields || []) :
      [];
  }
  const cfg = field.config || {};
  if (field.control === 'UgoInnerForm') {
    const childFields = typeof collectChildren === 'function' ?
      collectChildren(field.fields || []) :
      [];
    return buildValueDef({
      type: 'object',
      key: field.key,
      name: field.name,
      option: {
        model: cfg.model,
        description: `内嵌表单，JSON 键名必须为 "${field.key}"`,
      },
      fields: childFields,
    });
  }
  if (field.control === 'UgoMultiForm') {
    const childFields = typeof collectChildren === 'function' ?
      collectChildren(field.fields || []) :
      [];
    return buildValueDef({
      type: 'array',
      key: field.key,
      name: field.name,
      option: {
        itemType: 'object',
        model: cfg.model,
        description: `多重表(MultiForm)：键 "${field.key}" 的值为对象数组，每项结构见 fields`,
        valueShape: `[{ ${childFields.map((f) => f.key).filter(Boolean).join(', ')} }]`,
      },
      fields: childFields,
    });
  }
  if (field.control === 'UgoSelect' || field.control === 'UgoRadio') {
    const multiple = !!cfg.multiple;
    return buildValueDef({
      type: multiple ? 'array' : 'string',
      key: field.key,
      name: field.name,
      option: {
        ...selectOptionValueMeta(cfg),
        options: normalizeSelectOptions(cfg),
      },
    });
  }
  if (field.control === 'UgoCheck' || field.control === 'UgoMultiSelect') {
    return buildValueDef({
      type: 'array',
      key: field.key,
      name: field.name,
      option: {
        ...selectOptionValueMeta(cfg),
        options: normalizeSelectOptions(cfg),
      },
    });
  }
  const type = SCHEMA_CONTROL_TYPE[field.control] || 'string';
  return buildValueDef({
    type,
    key: field.key,
    name: field.name,
    option: {...cfg},
  });
}

export function nestedFormValueDef(field, getChildDef) {
  const fields = typeof getChildDef === 'function' ?
    getChildDef(field.fields || []) :
    [];
  return objectValueDef(field, fields, {
    model: field.config && field.config.model,
    description: '内嵌表单对象',
  });
}

export function multiFormValueDef(field, getChildDef) {
  const itemFields = typeof getChildDef === 'function' ?
    getChildDef(field.fields || []) :
    [];
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      itemType: 'object',
      model: field.config && field.config.model,
      description: `多重表(MultiForm)：键 "${field.key}" 的值为对象数组，每项为一条记录`,
      valueShape: `[{ ${itemFields.map((f) => f.key).filter(Boolean).join(', ')} }]`,
    },
    fields: itemFields,
  });
}

/** 仅收集 valueDef 顶层字段 key（不把 array 行内 fields 当作表单顶层键） */
export function collectTopLevelValueDefKeys(defs) {
  const keys = new Set();
  (defs || []).forEach((d) => {
    if (d && d.key) {
      keys.add(d.key);
    }
  });
  return keys;
}

const COL_CONTROL_VALUE_TYPE = {
  Input: 'string',
  UgoInput: 'string',
  Textarea: 'string',
  UgoTextarea: 'string',
  Number: 'number',
  UgoNumber: 'number',
  InputNumber: 'number',
  Date: 'string',
  UgoDate: 'string',
  Datetime: 'string',
  UgoDatetime: 'string',
  Month: 'string',
  UgoMonth: 'string',
  Select: 'string',
  UgoSelect: 'string',
  Radio: 'string',
  UgoRadio: 'string',
  Check: 'array',
  UgoCheck: 'array',
  MultiSelect: 'array',
  UgoMultiSelect: 'array',
  BaseSelect: 'string',
  'i-switch': 'boolean',
  Mark: 'number',
  UgoMark: 'number',
};

const TABLE_META_CONTROLS = new Set(['drag', 'index']);

export function colControlValueType(control) {
  if (!control) {
    return 'string';
  }
  return COL_CONTROL_VALUE_TYPE[control] || 'string';
}

export function buildTableColFields(cols = [], resolveColDef) {
  return cols
      .filter((col) => col && col.key && !TABLE_META_CONTROLS.has(col.control))
      .map((col) => {
        if (typeof resolveColDef === 'function') {
          const custom = resolveColDef(col);
          if (custom) {
            return custom;
          }
        }
        const type = colControlValueType(col.control);
        const def = buildValueDef({
          type,
          key: col.key,
          name: col.name || col.key,
          option: {
            control: col.control,
            ...(col.config || {}),
            ...(col.props || {}),
          },
        });
        const selectCfg = Object.assign({}, col.config || {}, col.props || {});
        if (selectCfg.options) {
          def.option.options = normalizeSelectOptions(selectCfg);
          Object.assign(def.option, selectOptionValueMeta(selectCfg));
        }
        return def;
      });
}

export function tableValueDef(field) {
  const cols = (field.config && field.config.cols) || [];
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      itemType: 'object',
      description: '表格行数组，每行键为列 key',
    },
    fields: buildTableColFields(cols),
  });
}

export function contractSplitTableValueDef(field) {
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      itemType: 'object',
      amountKey: field.config && field.config.amountKey,
    },
    fields: [
      {type: 'string', key: 'business_type', name: '服务类型', option: {options: ['全过程咨询', '项目管理', '房建监理', '市政监理', '造价咨询', 'BIM咨询', '招标代理', '对外合作', '其他']}},
      {type: 'number', key: 'amount', name: '金额'},
      {type: 'string', key: 'type', name: '类型', option: {options: ['自营', '联合体', '外委']}},
      {type: 'number', key: 'deduct_amount', name: '扣除金额'},
      {type: 'boolean', key: 'unbilling', name: '对方开票'},
    ],
  });
}

export function userSelectValueDef(field) {
  const multiple = !!(field.config && field.config.multiple);
  return buildValueDef({
    type: multiple ? 'array' : 'object',
    key: field.key,
    name: field.name,
    option: {
      multiple,
      description: multiple ? '用户 id 或用户对象数组' : '用户 id 或用户对象',
    },
  });
}

export function datetimeRangeValueDef(field) {
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      length: 2,
      itemType: 'string',
      description: '[开始时间, 结束时间]，格式 YYYY-MM-DD HH:mm:ss',
    },
  });
}

export function zzlPreselectValueDef(field) {
  return buildValueDef({
    type: 'string',
    key: field.key,
    name: field.name,
    option: {
      options: [
        {key: 0, name: '选用'},
        {key: 1, name: '不选用'},
        {key: 2, name: '自定义'},
      ],
    },
  });
}
